import { declaredFormFields, type FormFieldDeclaration } from '@h2hspark/contracts';
import { ApiRequestError, Button, Input, Label, Textarea, cn } from '@h2hspark/ui';
import * as Lucide from 'lucide-react';
import * as React from 'react';

import { usePublishedEntry, useSubmitForm } from './api';
import { ContentLink } from './content-link-panel';
import { Band, SectionLeadIn } from './section-band-panel';
import { list, text, type ContentData, type PublishedContent } from './types';

/**
 * **A form on a marketing page** (`CMS-016` AC-1, AC-6).
 *
 * ## What this renders, and what it may not
 *
 * *"A form entry declares its fields, and the marketing site renders exactly those."* Exactly
 * those: every declared field gets a control, and **no control exists here that is not a declared
 * field**. That second half is the one a reader should hold onto, because it is what rules out the
 * obvious anti-spam additions — a honeypot input, a challenge question, a CAPTCHA widget are all
 * controls the form did not declare. The limit on submissions is at the API instead (AC-3), where
 * the browser has no say in it, and the story records that reasoning.
 *
 * ## Why it fetches, like the collection sections next door
 *
 * The section holds a *slug*, not a field list — see `formEmbed` in `content-types.ts`. So this is
 * the third fetching section, and it follows the same shape `collection-sections-panel.tsx`
 * established: a container that asks for the entry, and a presentational component that takes the
 * form as a prop. The presentational half is what the tests render, which is how AC-1 and AC-6 are
 * assertions about real markup rather than about source text.
 *
 * ## An unpublished or missing form renders nothing
 *
 * Not an error, and not an empty band with a heading over it — the same call `CMS-015` AC-5 makes
 * for an empty collection, and for the same reason: a heading above a form that is not there reads
 * as a page that failed to load.
 */
export function FormSection({
  section,
}: {
  readonly section: ContentData;
}): React.ReactElement | null {
  const slug = text(section, 'formSlug');
  const form = usePublishedEntry('form', slug === '' ? undefined : slug);
  const aside = list(section, 'aside');
  if (form.data === undefined) return null;

  // ── One pane, or two ───────────────────────────────────────────────────────────────────────
  //
  // `Web App.dc.html` draws Contact at `minmax(0,1.4fr) minmax(0,1fr)` and Book a demo at
  // `minmax(0,1.45fr) minmax(0,1fr)`: the form on the left and a column of short cards on the
  // right — other ways to reach us, what to expect from the call. One ratio here rather than the
  // prototype's two, because the difference between 1.4 and 1.45 is four pixels at 1200 and two
  // numbers that can drift apart.
  //
  // Below `lg` it is one column in reading order: the form first, the cards under it. The cards
  // are context, and a visitor on a phone should not scroll past four of them to reach the thing
  // they came to fill in. Reflow, so no `responsive-behaviour.ts` entry — everything is reachable
  // at every width.
  //
  // A section with no cards renders exactly as it did before, which is what `/contact` did until
  // this change and what any other form embed still does.
  if (aside.length === 0) {
    return (
      <Band anchor={text(section, 'anchor')} tint="sunken">
        <SectionLeadIn
          eyebrow={text(section, 'eyebrow')}
          heading={text(section, 'heading')}
          intro={text(section, 'intro')}
        />
        <PublishedForm form={form.data} />
      </Band>
    );
  }

  return (
    <Band anchor={text(section, 'anchor')} tint="sunken">
      <SectionLeadIn
        eyebrow={text(section, 'eyebrow')}
        heading={text(section, 'heading')}
        intro={text(section, 'intro')}
      />
      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          {/* `wide`: inside two panes the form fills its own column rather than keeping the
              centred `max-w-xl` it uses when it is the whole band. */}
          <PublishedForm form={form.data} wide />
        </div>
        <FormAsideCards cards={aside} />
      </div>
    </Band>
  );
}

/**
 * One card beside a form — the prototype's *"Other ways to reach us"* and *"What to expect"* rows.
 *
 * Content, like everything else on these pages: an icon name, a title, a line under it, and an
 * optional link. **The link goes through `ContentLink`** like every address an editor types, which
 * is what lets a card address the demo page, or `mailto:hello@h2hspark.com` — the prototype prints
 * that address as plain text, which `handoff-corrections.md` #136 files as a defect on the grounds
 * that a reader cannot click it.
 *
 * `tint: 'brand'` draws the demo page's purple *"What to expect"* card; anything else is the white
 * card Contact uses. A role rather than a colour, for the reason the palette has refused authored
 * colour since it was written.
 */
/**
 * The column of cards, exported so it can be rendered on its own.
 *
 * `FormSection` fetches, which is why `PublishedForm` is the half the tests render — the pattern
 * `collection-sections-panel.tsx` set. This is the same split for the other half of the two panes.
 */
export function FormAsideCards({
  cards,
}: {
  readonly cards: ReadonlyArray<ContentData>;
}): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-3.5">
      {cards.map((card, index) => (
        <FormAsideCard key={index} card={card} />
      ))}
    </div>
  );
}

function FormAsideCard({ card }: { readonly card: ContentData }): React.ReactElement {
  const brand = text(card, 'tint') === 'brand';
  const label = text(card, 'linkLabel');
  const href = text(card, 'linkHref');
  const body = text(card, 'body');

  return (
    <div
      className={cn(
        'rounded-xl border p-5',
        brand ? 'border-transparent bg-brand-800 text-primary-foreground' : 'border-border bg-card',
      )}
    >
      <div className="flex items-start gap-3">
        <AsideGlyph name={text(card, 'icon')} brand={brand} />
        <div className="min-w-0">
          <p className={cn('font-brand text-md font-bold', brand ? 'text-primary-foreground' : '')}>
            {text(card, 'title')}
          </p>
          {body !== '' && (
            <p
              className={cn(
                'mt-1 text-sm',
                brand ? 'text-primary-foreground/85' : 'text-muted-foreground',
              )}
            >
              {body}
            </p>
          )}
          {label !== '' && href !== '' && (
            <ContentLink
              href={href}
              className={cn(
                'mt-2 inline-flex text-sm font-semibold underline-offset-4 hover:underline',
                brand ? 'text-fuchsia-200' : 'text-brand',
              )}
            >
              {label}
            </ContentLink>
          )}
        </div>
      </div>
    </div>
  );
}

/** A card's glyph. Absent or unknown draws nothing rather than an empty square. */
function AsideGlyph({
  name,
  brand,
}: {
  readonly name: string;
  readonly brand: boolean;
}): React.ReactElement | null {
  if (name === '') return null;
  const icons = Lucide as unknown as Record<string, React.ComponentType<{ className?: string }>>;
  const Icon = icons[name];
  if (Icon === undefined) return null;
  return (
    <Icon
      className={cn('h-5 w-5 shrink-0', brand ? 'text-fuchsia-200' : 'text-brand')}
      aria-hidden
    />
  );
}

/**
 * The form itself.
 *
 * ## The order of the last three elements is `CMS-016` AC-6
 *
 * *"The form states what happens to the information before it is submitted, not after."* So the
 * privacy note is the element immediately above the submit button, in the document, and the test
 * asserts the **order** rather than the presence. Putting it on the acknowledgement — the obvious
 * place, and where a thank-you message would naturally carry it — tells somebody what happened to
 * information they have already sent, which is the thing the criterion is worded to exclude.
 *
 * It is `required: true` on the content type, so there is no branch here for a form without one:
 * a form that does not say cannot be saved, let alone published.
 *
 * ## After a successful submission the form is replaced, not disabled
 *
 * A form left on screen after it has been sent invites a second send, which is the commonest way
 * a contact inbox gets the same question three times. `role="status"` so the acknowledgement is
 * announced rather than only appearing.
 */
export function PublishedForm({
  form,
  wide = false,
}: {
  readonly form: PublishedContent;
  /** Fill the column rather than keeping the centred width a band-wide form uses. */
  readonly wide?: boolean;
}): React.ReactElement {
  const fields = declaredFormFields(form.data);
  const [answers, setAnswers] = React.useState<Record<string, string | boolean | string[]>>({});
  const submit = useSubmitForm(form.slug);

  if (submit.data !== undefined) {
    return (
      <div
        role="status"
        className={cn(
          'rounded-lg border border-border bg-card p-6 text-center',
          wide ? '' : 'mx-auto max-w-xl',
        )}
      >
        <p className="text-md text-foreground">{submit.data.successMessage}</p>
      </div>
    );
  }

  const heading = text(form.data, 'heading');
  const description = text(form.data, 'description');
  const submitLabel = text(form.data, 'submitButtonLabel');

  return (
    <form
      className={cn('flex flex-col gap-5', wide ? '' : 'mx-auto max-w-xl')}
      onSubmit={(event) => {
        event.preventDefault();
        submit.mutate(answersToSend(fields, answers));
      }}
    >
      {/* `h2`, not `h3`. This form is a first-level section of its page — on `/contact` and
          `/book-a-demo` it is the only thing between the page `h1` and the side rail — so an `h3`
          left a level missing and a screen-reader user hearing "heading level 3" with no level 2
          above it (WCAG 1.3.1, `PLT-007`). Found in the parity pass, 2026-09-12. */}
      {heading !== '' && <h2 className="text-xl font-semibold tracking-tight">{heading}</h2>}
      {description !== '' && <p className="text-muted-foreground">{description}</p>}

      {fields.map((field) => (
        <FormField
          key={field.key}
          field={field}
          value={answers[field.key]}
          onChange={(next) => setAnswers((held) => ({ ...held, [field.key]: next }))}
        />
      ))}

      {/* The refusal, in the one place a person is looking when a send fails. `role="alert"` for
          the same reason every other refusal on this platform carries one. */}
      {submit.error !== null && submit.error !== undefined && (
        <p role="alert" className="text-sm text-destructive">
          {messageOf(submit.error)}
        </p>
      )}

      {/* AC-6. Above the button, in the document, and asserted as being above it. */}
      <p className="text-sm text-muted-foreground">{text(form.data, 'privacyNote')}</p>

      <Button type="submit" disabled={submit.isPending} className="self-start">
        {submit.isPending ? 'Sending…' : submitLabel === '' ? 'Send' : submitLabel}
      </Button>
    </form>
  );
}

/**
 * One declared field, as its declared control.
 *
 * **Native `<select>` and native `<input type="checkbox">`**, rather than the design system's
 * Radix-backed `Select` and `Checkbox`. Two reasons, and the second is the load-bearing one:
 *
 *   - This is the only form on this platform filled in by somebody with no account, on whatever
 *     they happen to be using. A native control works when a script does not.
 *   - A Radix select renders a button and a portal, so *"the marketing site renders exactly those
 *     fields"* would become an assertion about a widget rather than about a form. AC-1 is
 *     checkable because the markup is ordinary.
 *
 * The control classes are the design system's own, so it is the same control by sight.
 */
function FormField({
  field,
  value,
  onChange,
}: {
  readonly field: FormFieldDeclaration;
  readonly value: string | boolean | ReadonlyArray<string> | undefined;
  readonly onChange: (next: string | boolean | string[]) => void;
}): React.ReactElement {
  const id = `form-field-${field.key}`;

  /**
   * **Many of a fixed set** — the demo form's *"When suits you?"*, four chips under *"Pick all
   * that work and we'll send times that fit."*
   *
   * A `fieldset` with a `legend`, and one ordinary checkbox per choice. The prototype draws these
   * as toggle chips, and underneath they are checkboxes because that is what they are: a chip
   * that is a `<button>` with a colour change is a control a screen reader announces as a button
   * with no state, and this is the one form on the platform filled in by somebody with no account
   * on whatever they happen to be using.
   *
   * The chip's *look* is the label, drawn off the input's own `:checked` — so the design is the
   * design and the semantics are still a checkbox group.
   */
  if (field.control === 'checkboxGroup') {
    const chosen = Array.isArray(value) ? value : [];
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium leading-none">{field.label}</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {field.options.map((option) => (
            <label
              key={option.value}
              className={cn(
                'inline-flex cursor-pointer items-center gap-2 rounded-pill border border-border px-3.5 py-2 text-sm',
                'has-[:checked]:border-brand has-[:checked]:bg-brand-50 has-[:checked]:text-brand',
                'has-[:focus-visible]:outline has-[:focus-visible]:outline-2',
                'has-[:focus-visible]:outline-offset-2',
              )}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={chosen.includes(option.value)}
                onChange={(event) => {
                  const next = event.target.checked
                    ? [...chosen, option.value]
                    : chosen.filter((one) => one !== option.value);
                  onChange(next);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  if (field.control === 'checkbox') {
    return (
      <div className="flex items-start gap-3">
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          required={field.required}
          onChange={(event) => onChange(event.target.checked)}
          className="mt-1 h-4 w-4 rounded border-border"
        />
        <Label htmlFor={id}>{field.label}</Label>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{field.label}</Label>
      {field.control === 'textarea' ? (
        <Textarea
          id={id}
          rows={5}
          required={field.required}
          {...(field.placeholder === undefined ? {} : { placeholder: field.placeholder })}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : field.control === 'select' ? (
        <select
          id={id}
          required={field.required}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
          className={cn(
            'h-10 w-full rounded-md border border-border bg-card px-3 text-md text-foreground',
            'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-ring/40',
          )}
        >
          {/* An empty first option so a required select genuinely refuses an unmade choice,
              rather than silently submitting whichever option happened to be first. */}
          <option value="">Choose one</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <Input
          id={id}
          // `email` and `number` are the declared control, so the browser's own keyboard and its
          // own check follow from the content rather than from a guess about the field's name.
          type={
            field.control === 'email' ? 'email' : field.control === 'number' ? 'number' : 'text'
          }
          required={field.required}
          {...(field.placeholder === undefined ? {} : { placeholder: field.placeholder })}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}

/**
 * What goes on the wire: the declared keys, and only those.
 *
 * The API closes the answers against the form again — an undeclared key is refused there, which is
 * where it matters, because the browser is not a boundary. Doing it here as well means a visitor
 * never sees a validation failure caused by state this component was holding.
 *
 * An untouched checkbox is sent as `false` rather than omitted, so a required consent box cannot
 * be satisfied by not sending it; a text field left empty is omitted, so the API reports it as the
 * required field it is rather than as an empty string it must interpret.
 */
function answersToSend(
  fields: ReadonlyArray<FormFieldDeclaration>,
  held: Readonly<Record<string, string | boolean | ReadonlyArray<string>>>,
): Record<string, string | number | boolean | ReadonlyArray<string>> {
  const answers: Record<string, string | number | boolean | ReadonlyArray<string>> = {};
  for (const field of fields) {
    const value = held[field.key];
    // The chosen values, as an array. The API stores them joined and in the **form's** own order,
    // so two people who ticked the same boxes produce the same answer; this end sends what was
    // ticked and does not decide the order.
    if (field.control === 'checkboxGroup') {
      if (Array.isArray(value) && value.length > 0) answers[field.key] = value;
      continue;
    }
    if (field.control === 'checkbox') {
      answers[field.key] = value === true;
      continue;
    }
    if (typeof value !== 'string' || value.trim() === '') continue;
    if (field.control === 'number') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) answers[field.key] = parsed;
      continue;
    }
    answers[field.key] = value.trim();
  }
  return answers;
}

/**
 * The API's own sentence where there is one — a refusal names the field that failed, and
 * replacing that with house copy would take the useful half away.
 *
 * **`toUserMessage()`, not `.message`.** That was the intent all along and `.message` was not it:
 * on an `ApiRequestError` it is the transport's own summary — "Request failed with status 400." —
 * while the sentence the API wrote sits unread in the error envelope, which is precisely the
 * useful half this function exists to keep. A visitor told a status code cannot fix their
 * submission; one told which choice was rejected can.
 *
 * Anything that is not an `ApiRequestError` still falls through to `.message`, because a
 * transport failure genuinely has nothing better to say.
 */
function messageOf(error: unknown): string {
  if (error instanceof ApiRequestError) return error.toUserMessage();
  if (error !== null && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return 'That could not be sent. Please try again.';
}
